import React, { useMemo } from 'react'
import {
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tag,
  Tile,
} from '@carbon/react'
import { useClassesBatch } from '@sphere/school/data-access'
import {
  formatAnalyticsPercentOneDecimal,
  successRateCarbonTagType,
} from '@sphere/shared/util'
import styles from './EstablishmentAnalytics.module.scss'

export interface ClassMetrics {
  class_id: string
  student_count: number
  unique_activities: number
  total_attempts: number
  avg_score: number
  passed_attempts: number
  success_rate: number
}

export interface EstablishmentClassesTableProps {
  classes: ClassMetrics[]
}

export function EstablishmentClassesTable({
  classes,
}: EstablishmentClassesTableProps) {
  const classIds = useMemo(
    () => [...new Set(classes.map(c => c.class_id))],
    [classes]
  )
  const { data: lightClasses = [] } = useClassesBatch(classIds)
  const classNameMap = useMemo(
    () => Object.fromEntries(lightClasses.map(c => [c.id, c.name])),
    [lightClasses]
  )

  const headers = [
    { key: 'class', header: 'Classe' },
    { key: 'students', header: 'Élèves' },
    { key: 'activities', header: 'Activités' },
    { key: 'attempts', header: 'Tentatives' },
    { key: 'avgScore', header: 'Score moyen' },
    { key: 'successRate', header: 'Taux de réussite' },
  ]

  const rows = classes.map(classData => ({
    id: classData.class_id,
    class: classNameMap[classData.class_id] ?? classData.class_id,
    students: classData.student_count,
    activities: classData.unique_activities,
    attempts: classData.total_attempts,
    avgScore: formatAnalyticsPercentOneDecimal(classData.avg_score),
    successRate: formatAnalyticsPercentOneDecimal(classData.success_rate),
    successRateValue: classData.success_rate,
  }))

  if (classes.length === 0) {
    return (
      <Tile className={styles.emptyState}>
        <p>Aucune classe disponible sur la période sélectionnée.</p>
      </Tile>
    )
  }

  return (
    <Tile className={styles.tableTile}>
      <h3 className={styles.sectionTitle}>Performance par classe</h3>
      <DataTable rows={rows} headers={headers} isSortable>
        {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
          <Table {...getTableProps()}>
            <TableHead>
              <TableRow>
                {headers.map(header => {
                  const { key: _key, ...headerProps } = getHeaderProps({
                    header,
                  })
                  return (
                    <TableHeader key={header.key} {...headerProps}>
                      {header.header}
                    </TableHeader>
                  )
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => {
                const successRateValue = row.cells.find(c =>
                  c.id.includes('successRateValue')
                )?.value as number
                const { key: _rowKey, ...rowProps } = getRowProps({ row })
                return (
                  <TableRow key={row.id} {...rowProps}>
                    {row.cells.map(cell => {
                      if (cell.info.header === 'successRate') {
                        const tagType = successRateCarbonTagType(successRateValue)
                        return (
                          <TableCell key={cell.id}>
                            <Tag type={tagType === 'yellow' ? 'teal' : tagType}>
                              {cell.value}
                            </Tag>
                          </TableCell>
                        )
                      }
                      return <TableCell key={cell.id}>{cell.value}</TableCell>
                    })}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </DataTable>
    </Tile>
  )
}
